import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import { analyticsOverviewService } from "@/services/analytics-overview-service";
import type { OfferConversionSide } from "@/types/analytics-overview";
import { useAnalyticsOverviewRange } from "./useAnalyticsOverviewRange";

export const analyticsOverviewKeys = {
  all: ["analytics-overview"] as const,
  offerConversion: (
    hotelId: string,
    from: string,
    to: string,
    side?: OfferConversionSide,
  ) =>
    [
      ...analyticsOverviewKeys.all,
      "offer-conversion",
      hotelId,
      from,
      to,
      side,
    ] as const,
  ratingsByStay: (hotelId: string, from: string, to: string) =>
    [
      ...analyticsOverviewKeys.all,
      "ratings-by-stay",
      hotelId,
      from,
      to,
    ] as const,
  ordersByDay: (
    hotelId: string,
    from: string,
    to: string,
    breakdownByType?: boolean,
  ) =>
    [
      ...analyticsOverviewKeys.all,
      "orders-by-day",
      hotelId,
      from,
      to,
      breakdownByType ?? false,
    ] as const,
  preCheckInByDay: (hotelId: string, from: string, to: string) =>
    [
      ...analyticsOverviewKeys.all,
      "pre-check-in-by-day",
      hotelId,
      from,
      to,
    ] as const,
  preCheckOutByDay: (hotelId: string, from: string, to: string) =>
    [
      ...analyticsOverviewKeys.all,
      "pre-check-out-by-day",
      hotelId,
      from,
      to,
    ] as const,
};

const EMPTY_DATE_RANGE: DateRange = { from: undefined, to: undefined };

export function useOfferConversionOverview(
  dateRange: DateRange,
  side: OfferConversionSide = "ALL",
) {
  const { hotelId, from, to, isReady } = useAnalyticsOverviewRange(dateRange);

  return useQuery({
    queryKey: analyticsOverviewKeys.offerConversion(hotelId, from, to, side),
    queryFn: () =>
      analyticsOverviewService.getOfferConversion({ hotelId, from, to, side }),
    enabled: isReady,
  });
}

export function useRatingsByStayOverview(dateRange: DateRange) {
  const { hotelId, from, to, isReady } = useAnalyticsOverviewRange(dateRange);

  return useQuery({
    queryKey: analyticsOverviewKeys.ratingsByStay(hotelId, from, to),
    queryFn: () =>
      analyticsOverviewService.getRatingsByStay({ hotelId, from, to }),
    enabled: isReady,
  });
}

export function useOrdersByDayOverview(
  dateRange: DateRange,
  breakdownByType = false,
) {
  const { hotelId, from, to, isReady } = useAnalyticsOverviewRange(dateRange);

  return useQuery({
    queryKey: analyticsOverviewKeys.ordersByDay(
      hotelId,
      from,
      to,
      breakdownByType,
    ),
    queryFn: () =>
      analyticsOverviewService.getOrdersByDay({
        hotelId,
        from,
        to,
        breakdownByType,
      }),
    enabled: isReady,
  });
}

export function useOverviewAnalytics(dateRange: DateRange) {
  const rangeParams = useAnalyticsOverviewRange(dateRange);
  const offerConversion = useOfferConversionOverview(dateRange);
  const ratingsByStay = useRatingsByStayOverview(dateRange);
  const ordersByDay = useOrdersByDayOverview(dateRange);
  const preCheckInByDay = usePreCheckInByDayOverview(dateRange);
  const preCheckOutByDay = usePreCheckOutByDayOverview(dateRange);

  return {
    ...rangeParams,
    offerConversion,
    ratingsByStay,
    ordersByDay,
    preCheckInByDay,
    preCheckOutByDay,
    isLoading:
      offerConversion.isLoading ||
      ratingsByStay.isLoading ||
      ordersByDay.isLoading ||
      preCheckInByDay.isLoading ||
      preCheckOutByDay.isLoading,
    isFetching:
      offerConversion.isFetching ||
      ratingsByStay.isFetching ||
      ordersByDay.isFetching ||
      preCheckInByDay.isFetching ||
      preCheckOutByDay.isFetching,
    isError:
      offerConversion.isError ||
      ratingsByStay.isError ||
      ordersByDay.isError ||
      preCheckInByDay.isError ||
      preCheckOutByDay.isError,
    error:
      offerConversion.error ??
      ratingsByStay.error ??
      ordersByDay.error ??
      preCheckInByDay.error ??
      preCheckOutByDay.error,
  };
}

export function usePreCheckInByDayOverview(dateRange: DateRange) {
  const { hotelId, from, to, isReady } = useAnalyticsOverviewRange(dateRange);

  return useQuery({
    queryKey: analyticsOverviewKeys.preCheckInByDay(hotelId, from, to),
    queryFn: () =>
      analyticsOverviewService.getPreCheckInByDay({ hotelId, from, to }),
    enabled: isReady,
  });
}

export function usePreCheckOutByDayOverview(dateRange: DateRange) {
  const { hotelId, from, to, isReady } = useAnalyticsOverviewRange(dateRange);

  return useQuery({
    queryKey: analyticsOverviewKeys.preCheckOutByDay(hotelId, from, to),
    queryFn: () =>
      analyticsOverviewService.getPreCheckOutByDay({ hotelId, from, to }),
    enabled: isReady,
  });
}

export { EMPTY_DATE_RANGE };
